import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/novels';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCategoryOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setCategoryOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isHome = location.pathname === '/';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || !isHome
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap cursor-pointer">
            <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center">
              <i className={`ri-book-3-line text-xl md:text-2xl ${scrolled || !isHome ? 'text-primary-500' : 'text-background-50'}`}></i>
            </div>
            <span
              className={`font-heading text-lg md:text-xl font-bold tracking-wide ${
                scrolled || !isHome ? 'text-foreground-900' : 'text-background-50'
              }`}
            >
              都市美艳后宫
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                location.pathname === '/'
                  ? scrolled || !isHome ? 'text-primary-600' : 'text-primary-300'
                  : scrolled || !isHome ? 'text-foreground-700 hover:text-primary-500' : 'text-background-50/90 hover:text-background-50'
              }`}
            >
              首页
            </Link>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setCategoryOpen(!categoryOpen)}
                className={`flex items-center gap-1 text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                  location.pathname.startsWith('/category')
                    ? scrolled || !isHome ? 'text-primary-600' : 'text-primary-300'
                    : scrolled || !isHome ? 'text-foreground-700 hover:text-primary-500' : 'text-background-50/90 hover:text-background-50'
                }`}
              >
                分类浏览
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${categoryOpen ? 'rotate-180' : ''}`}></i>
                </div>
              </button>
              {categoryOpen && (
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 bg-background-50 border border-background-200/70 rounded-lg shadow-lg py-2 w-48 z-50">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors duration-150 whitespace-nowrap cursor-pointer"
                    >
                      <span className="w-4 h-4 inline-flex items-center justify-center mr-2">
                        <i className={`${cat.icon} text-xs`}></i>
                      </span>
                      {cat.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/about"
              className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                location.pathname === '/about'
                  ? scrolled || !isHome ? 'text-primary-600' : 'text-primary-300'
                  : scrolled || !isHome ? 'text-foreground-700 hover:text-primary-500' : 'text-background-50/90 hover:text-background-50'
              }`}
            >
              关于我们
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <div className="relative">
              <div className="w-5 h-5 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <i className={`ri-search-line text-sm ${scrolled || !isHome ? 'text-foreground-400' : 'text-background-50/60'}`}></i>
              </div>
              <input
                type="text"
                placeholder="搜索小说..."
                className={`pl-9 pr-4 py-2 text-sm rounded-full border outline-none transition-all duration-200 w-44 focus:w-56 ${
                  scrolled || !isHome
                    ? 'bg-background-100 border-background-200 text-foreground-800 placeholder-foreground-400 focus:border-primary-300'
                    : 'bg-background-50/15 border-background-50/20 text-background-50 placeholder-background-50/50 focus:bg-background-50/25'
                }`}
              />
            </div>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${
              scrolled || !isHome ? 'text-foreground-800' : 'text-background-50'
            }`}
            aria-label="菜单"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70">
          <div className="px-4 py-3 space-y-1">
            <Link to="/" className="block py-3 px-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-lg transition-colors cursor-pointer">
              首页
            </Link>
            <div className="py-2 px-3 text-sm font-semibold text-foreground-400">分类浏览</div>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="block py-2.5 px-3 pl-7 text-sm text-foreground-600 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
              >
                <span className="w-4 h-4 inline-flex items-center justify-center mr-2">
                  <i className={`${cat.icon} text-xs`}></i>
                </span>
                {cat.name}
              </Link>
            ))}
            <Link to="/about" className="block py-3 px-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-lg transition-colors cursor-pointer">
              关于我们
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}